import type { useGetOrdersList } from "@birdwatch/client";

import { OrderStatus } from "@/types/openapi-enum";

/** Page size for each place order status group (load-more increment). */
export const PLACE_ORDER_GROUP_PAGE_SIZE = 10;

/** Includes needed to render an order card inside a place order group. */
export const PLACE_ORDER_INCLUDES = "status,tags";

type OrdersListBuilder = ReturnType<typeof useGetOrdersList>;

/** Future ("Proposed") work sorts by scheduled date; everything else by recency. */
const sortForStatus = (statusKey: string) =>
  statusKey === OrderStatus.Proposed ? "date" : "-updatedAt";

/**
 * Active status group query for the place "Orders" tab (one open status).
 *
 * Pure params only — the terminal `getInfinite()` runs in
 * `_api/use-orders.ts`, so every caller shares one query key.
 */
export const buildPlaceOrderStatusQuery = (
  listQuery: OrdersListBuilder,
  params: { placeId: string; status: { id: string; statusKey: string } },
) =>
  listQuery
    .filters({
      placeId: params.placeId,
      statusId: params.status.id,
      showDraft: "true",
      showUpcoming: "true",
    })
    .sort(sortForStatus(params.status.statusKey))
    .includes(PLACE_ORDER_INCLUDES)
    .take(PLACE_ORDER_GROUP_PAGE_SIZE)
    .withCount();

/**
 * Past orders query for the place "Orders" tab — Done / Cancelled / Deferred in
 * a single collapsible group, newest update first.
 */
export const buildPlacePastOrdersQuery = (
  listQuery: OrdersListBuilder,
  params: { placeId: string; statusKeys: string[] },
) =>
  listQuery
    .filters({
      placeId: params.placeId,
      statusKey: { in: params.statusKeys.join(",") },
      showDeferred: "true",
    })
    .sort("-updatedAt")
    .includes(PLACE_ORDER_INCLUDES)
    .take(PLACE_ORDER_GROUP_PAGE_SIZE)
    .withCount();
